import { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import { DAY_NAMES } from '../hooks/useOneOnOneRelationships';
import { useAccountLinks } from '../hooks/useAccountLinks';

export default function AddRelationshipModal({ isOpen, onClose, onSave, editItem = null }) {
  const { activeLinks, getLinkedUserInfo } = useAccountLinks();

  // Build list of linkable users (all active linked accounts)
  const linkableUsers = useMemo(() => {
    return activeLinks.map(link => {
      const info = getLinkedUserInfo(link);
      return {
        uid: info.userId,
        email: info.email,
        displayName: info.displayName,
        firstName: info.displayName?.split(' ')[0] || 'User'
      };
    });
  }, [activeLinks, getLinkedUserInfo]);

  const [formData, setFormData] = useState({
    personName: '',
    meetingDay: 2, // Default to Tuesday
    linkedUserId: '', // Empty string means no link
    linkedUserEmail: '',
    showGoals: true // Whether to show Goals section in this 1:1
  });

  useEffect(() => {
    if (editItem) {
      setFormData({
        personName: editItem.personName || '',
        meetingDay: editItem.meetingDay ?? 2,
        linkedUserId: editItem.linkedUserId || '',
        linkedUserEmail: editItem.linkedUserEmail || '',
        showGoals: editItem.showGoals !== false // Default to true for existing items
      });
    } else {
      setFormData({
        personName: '',
        meetingDay: 2,
        linkedUserId: '',
        linkedUserEmail: '',
        showGoals: true
      });
    }
  }, [editItem, isOpen]);

  // Handle linked user selection
  const handleLinkedUserChange = (e) => {
    const selectedUid = e.target.value;
    if (!selectedUid) {
      setFormData({ ...formData, linkedUserId: '', linkedUserEmail: '' });
    } else {
      const selectedUser = linkableUsers.find(u => u.uid === selectedUid);
      setFormData({
        ...formData,
        linkedUserId: selectedUid,
        linkedUserEmail: selectedUser?.email || ''
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.personName.trim()) return;
    onSave(formData);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? 'Edit 1:1 Relationship' : 'Add 1:1 Relationship'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-[13px] font-medium mb-1.5 text-text">
            Person Name
          </label>
          <input
            type="text"
            placeholder="Enter name"
            value={formData.personName}
            onChange={(e) => setFormData({ ...formData, personName: e.target.value })}
            required
            autoFocus
            className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none focus:border-orange bg-surface-base text-text-primary"
          />
        </div>

        <div>
          <label className="block text-[13px] font-medium mb-1.5 text-text">
            Meeting Day
          </label>
          <select
            value={formData.meetingDay}
            onChange={(e) => setFormData({ ...formData, meetingDay: parseInt(e.target.value) })}
            className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none bg-surface-base text-text-primary focus:border-orange cursor-pointer"
          >
            {DAY_NAMES.map((day, index) => (
              <option key={index} value={index}>
                {day}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-text-tertiary mt-1.5">
            Your 1:1 week will run from {DAY_NAMES[formData.meetingDay]} to {DAY_NAMES[(formData.meetingDay + 6) % 7]}
          </p>
        </div>

        {linkableUsers.length > 0 && (
          <div>
            <label className="block text-[13px] font-medium mb-1.5 text-text">
              Link to User (Optional)
            </label>
            <select
              value={formData.linkedUserId}
              onChange={handleLinkedUserChange}
              className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none bg-surface-base text-text-primary focus:border-orange cursor-pointer"
            >
              <option value="">None (external person)</option>
              {linkableUsers.map((user) => (
                <option key={user.uid} value={user.uid}>
                  {user.displayName || user.firstName}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-text-tertiary mt-1.5">
              {formData.linkedUserId
                ? 'This person will be able to see and contribute to this 1:1'
                : 'Leave unlinked for 1:1s with external people (clients, partners, etc.)'}
            </p>
          </div>
        )}

        <div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showGoals}
              onChange={(e) => setFormData({ ...formData, showGoals: e.target.checked })}
              className="w-4 h-4 rounded border-border accent-orange cursor-pointer"
            />
            <span className="text-[13px] font-medium text-text">Show Goals</span>
          </label>
          <p className="text-[11px] text-text-tertiary mt-1 ml-6">
            Display the Goals section during this 1:1
          </p>
        </div>

        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-5 bg-grey-light text-text border-none rounded-md text-sm font-medium cursor-pointer hover:bg-border"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-5 bg-orange text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-hover"
          >
            {editItem ? 'Save Changes' : 'Add Person'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
